import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Package, Plus, Edit3, AlertTriangle } from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import {
  EMPTY_EXPEDITION_SETTINGS_FORM,
  buildExpeditionSettingsUpdatePayload,
  normalizeExpeditionSettingsForForm,
} from '../utils/expeditionSettingsPayload';
import {
  buildExpeditionProfileCreatePayloadFromSubmit,
  buildExpeditionProfileUpdatePayload,
  createEmptyExpeditionProfileForm,
  normalizeExpeditionProfileForForm,
  resolveExpeditionProfileNameForSubmit,
} from '../utils/expeditionProfilePayload';

const PROFILE_NAME_INPUT_ID = 'expedition-profile-name';

/** Classe Admin standard (index.css `.input-field`) — ne pas utiliser `.input` (non définie). */
const EXPEDITION_FORM_INPUT_CLASS = 'input-field w-full bg-white';

function ExpeditionFormField({ label, className = '', ...inputProps }) {
  const inputId = React.useId();
  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input id={inputId} className={EXPEDITION_FORM_INPUT_CLASS} {...inputProps} />
    </div>
  );
}

const ExpeditionManager = ({ commercantId }) => {
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [settings, setSettings] = useState(EMPTY_EXPEDITION_SETTINGS_FORM);
  const [profiles, setProfiles] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(createEmptyExpeditionProfileForm);
  const profileNameInputRef = useRef(null);

  const activeProfiles = useMemo(
    () => profiles.filter((profile) => profile.isActive),
    [profiles],
  );

  useEffect(() => {
    if (!commercantId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [settingsRes, profilesRes] = await Promise.all([
        apiService.getExpeditionSettings(commercantId),
        apiService.listExpeditionProfiles(commercantId),
      ]);
      const raw = settingsRes?.settings || {};
      setSettings(normalizeExpeditionSettingsForForm(raw));
      setProfiles(profilesRes?.items || []);
    } catch (err) {
      setError(err?.message || 'Impossible de charger la configuration Expédition.');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setFeedback('');
      setError('');
      const payload = buildExpeditionSettingsUpdatePayload(settings, commercantId);
      const response = await apiService.updateExpeditionSettings(payload);
      const saved = response?.settings || payload;
      setSettings(normalizeExpeditionSettingsForForm(saved));
      setFeedback('Configuration Expédition enregistrée.');
    } catch (err) {
      setError(err?.message || 'Erreur lors de l’enregistrement.');
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreateProfile = () => {
    setEditingProfile(null);
    setProfileForm(createEmptyExpeditionProfileForm());
    setProfileModalOpen(true);
  };

  const openEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm(
      normalizeExpeditionProfileForForm({
        name: profile.name || '',
        description: profile.description || '',
        weightGrams: profile.weightGrams || 500,
        lengthMm: profile.lengthMm || 200,
        widthMm: profile.widthMm || 150,
        heightMm: profile.heightMm || 100,
      }),
    );
    setProfileModalOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setError('');
      const domName = profileNameInputRef.current?.value;
      if (editingProfile?.id) {
        const resolvedName = resolveExpeditionProfileNameForSubmit(profileForm.name, domName);
        const nameOpts = resolvedName ? { nameOverride: resolvedName } : {};
        const body = buildExpeditionProfileUpdatePayload(profileForm, nameOpts);
        await apiService.updateExpeditionProfile(editingProfile.id, commercantId, body);
      } else {
        const payload = buildExpeditionProfileCreatePayloadFromSubmit(
          profileForm,
          commercantId,
          domName,
        );
        await apiService.createExpeditionProfile(payload);
      }
      setProfileModalOpen(false);
      await loadData();
      setFeedback(editingProfile ? 'Profil colis mis à jour.' : 'Profil colis créé.');
    } catch (err) {
      setError(err?.message || 'Erreur profil colis.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleProfile = async (profile) => {
    try {
      setError('');
      await apiService.setExpeditionProfileStatus(profile.id, commercantId, !profile.isActive);
      await loadData();
      setFeedback(profile.isActive ? 'Profil colis désactivé.' : 'Profil colis réactivé.');
    } catch (err) {
      setError(err?.message || 'Erreur statut profil colis.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8">
        <Loader2 className="h-5 w-5 animate-spin" />
        Chargement Expédition…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Package className="h-5 w-5" />
            Expédition
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configurez l’expédition colis (transporteur et checkout viendront plus tard).
          </p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center shrink-0">
          <input
            type="checkbox"
            checked={Boolean(settings.enabled)}
            onChange={(e) => handleSettingsChange('enabled', e.target.checked)}
            className="peer sr-only"
            aria-label="Activer l’expédition commerciale"
          />
          <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          <span className="ml-3 text-sm font-medium text-gray-700">
            {settings.enabled ? 'Activée' : 'Désactivée'}
          </span>
        </label>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {feedback && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {feedback}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card space-y-4">
          <h3 className="font-medium text-gray-900">Adresse expéditeur</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d’adresse d’expédition
              </label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={settings.senderAddressType || 'BUSINESS'}
                onChange={(e) => handleSettingsChange('senderAddressType', e.target.value)}
              >
                <option value="BUSINESS">Professionnelle</option>
                <option value="RESIDENTIAL">Résidentielle</option>
              </select>
            </div>
            <ExpeditionFormField label="Prénom du contact" value={settings.senderFirstName} onChange={(e) => handleSettingsChange('senderFirstName', e.target.value)} />
            <ExpeditionFormField label="Nom du contact" value={settings.senderLastName} onChange={(e) => handleSettingsChange('senderLastName', e.target.value)} />
            <ExpeditionFormField label="Nom" value={settings.senderName} onChange={(e) => handleSettingsChange('senderName', e.target.value)} />
            <ExpeditionFormField label="Société" value={settings.senderCompany} onChange={(e) => handleSettingsChange('senderCompany', e.target.value)} />
            <ExpeditionFormField className="md:col-span-2" label="Rue" value={settings.senderStreet} onChange={(e) => handleSettingsChange('senderStreet', e.target.value)} />
            <ExpeditionFormField label="Code postal" value={settings.senderPostalCode} onChange={(e) => handleSettingsChange('senderPostalCode', e.target.value)} />
            <ExpeditionFormField label="Ville" value={settings.senderCity} onChange={(e) => handleSettingsChange('senderCity', e.target.value)} />
            <ExpeditionFormField label="Pays (FR)" value={settings.senderCountry} onChange={(e) => handleSettingsChange('senderCountry', e.target.value)} />
            <ExpeditionFormField label="Téléphone" value={settings.senderPhone} onChange={(e) => handleSettingsChange('senderPhone', e.target.value)} />
            <ExpeditionFormField className="md:col-span-2" label="Email" type="email" autoComplete="off" value={settings.senderEmail} onChange={(e) => handleSettingsChange('senderEmail', e.target.value)} />
          </div>
        </div>

        <div className="card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-medium text-gray-900">Adresse retour</h3>
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={Boolean(settings.returnSameAsSender)}
                onChange={(e) => handleSettingsChange('returnSameAsSender', e.target.checked)}
              />
              Identique à l’expéditeur
            </label>
          </div>
          {!settings.returnSameAsSender && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ExpeditionFormField label="Nom" value={settings.returnName} onChange={(e) => handleSettingsChange('returnName', e.target.value)} />
              <ExpeditionFormField label="Société" value={settings.returnCompany} onChange={(e) => handleSettingsChange('returnCompany', e.target.value)} />
              <ExpeditionFormField className="md:col-span-2" label="Rue" value={settings.returnStreet} onChange={(e) => handleSettingsChange('returnStreet', e.target.value)} />
              <ExpeditionFormField label="Code postal" value={settings.returnPostalCode} onChange={(e) => handleSettingsChange('returnPostalCode', e.target.value)} />
              <ExpeditionFormField label="Ville" value={settings.returnCity} onChange={(e) => handleSettingsChange('returnCity', e.target.value)} />
              <ExpeditionFormField label="Pays" value={settings.returnCountry} onChange={(e) => handleSettingsChange('returnCountry', e.target.value)} />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Délai préparation (jours)
              </label>
              <input
                type="number"
                min="0"
                max="30"
                className={EXPEDITION_FORM_INPUT_CLASS}
                value={settings.preparationDelayDays}
                onChange={(e) => handleSettingsChange('preparationDelayDays', Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h3 className="font-medium text-gray-900">Tarification client</h3>
            <p className="text-sm text-gray-500 mt-1">
              Ces montants correspondent aux frais facturés à votre client.
              Les coûts transporteur seront gérés séparément.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frais d&apos;expédition TTC (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={EXPEDITION_FORM_INPUT_CLASS}
                placeholder="Ex. 5,90 — laisser vide si non configuré"
                value={settings.baseShippingFeeTtc}
                onChange={(e) => handleSettingsChange('baseShippingFeeTtc', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seuil d&apos;expédition gratuite TTC (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={EXPEDITION_FORM_INPUT_CLASS}
                placeholder="Optionnel"
                value={settings.freeShippingThresholdTtc}
                onChange={(e) => handleSettingsChange('freeShippingThresholdTtc', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary"
          onClick={handleSaveSettings}
          disabled={savingSettings}
        >
          {savingSettings ? 'Enregistrement…' : 'Enregistrer la configuration'}
        </button>
      </div>

      <div className="card space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-medium text-gray-900">Profils colis</h3>
            <p className="text-sm text-gray-500">
              {activeProfiles.length} actif(s) — associez un profil à un produit pour le rendre expédiable.
            </p>
          </div>
          <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={openCreateProfile}>
            <Plus className="h-4 w-4" />
            Nouveau profil
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">Nom</th>
                <th className="py-2 pr-4">Poids</th>
                <th className="py-2 pr-4">Dimensions (mm)</th>
                <th className="py-2 pr-4">Statut</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    Aucun profil colis. Créez-en un pour préparer l’expédition produits.
                  </td>
                </tr>
              ) : (
                profiles.map((profile) => (
                  <tr key={profile.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{profile.name}</div>
                      {profile.description ? (
                        <div className="text-xs text-gray-500">{profile.description}</div>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4">{profile.weightGrams} g</td>
                    <td className="py-3 pr-4">
                      {profile.lengthMm} × {profile.widthMm} × {profile.heightMm}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          profile.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {profile.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-gray-600 hover:text-gray-900"
                          onClick={() => openEditProfile(profile)}
                          aria-label={`Modifier ${profile.name}`}
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-sm text-blue-600 hover:text-blue-800"
                          onClick={() => handleToggleProfile(profile)}
                        >
                          {profile.isActive ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        title={editingProfile ? 'Modifier le profil colis' : 'Nouveau profil colis'}
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700" htmlFor={PROFILE_NAME_INPUT_ID}>
            Nom du profil
            <input
              id={PROFILE_NAME_INPUT_ID}
              ref={profileNameInputRef}
              className={`${EXPEDITION_FORM_INPUT_CLASS} mt-1`}
              placeholder="Ex. Colis test B2.2"
              autoComplete="off"
              value={profileForm.name}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
              onInput={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <textarea
            className={EXPEDITION_FORM_INPUT_CLASS}
            rows={2}
            placeholder="Description (optionnelle)"
            value={profileForm.description}
            onChange={(e) => setProfileForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              Poids (g)
              <input
                type="number"
                min="1"
                className={`${EXPEDITION_FORM_INPUT_CLASS} mt-1`}
                value={profileForm.weightGrams}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, weightGrams: e.target.value }))}
              />
            </label>
            <label className="text-sm">
              Longueur (mm)
              <input
                type="number"
                min="1"
                className={`${EXPEDITION_FORM_INPUT_CLASS} mt-1`}
                value={profileForm.lengthMm}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, lengthMm: e.target.value }))}
              />
            </label>
            <label className="text-sm">
              Largeur (mm)
              <input
                type="number"
                min="1"
                className={`${EXPEDITION_FORM_INPUT_CLASS} mt-1`}
                value={profileForm.widthMm}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, widthMm: e.target.value }))}
              />
            </label>
            <label className="text-sm">
              Hauteur (mm)
              <input
                type="number"
                min="1"
                className={`${EXPEDITION_FORM_INPUT_CLASS} mt-1`}
                value={profileForm.heightMm}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, heightMm: e.target.value }))}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setProfileModalOpen(false)}>
              Annuler
            </button>
            <button type="button" className="btn-primary" onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ExpeditionManager;
